import React, { useState, useEffect, useContext } from "react";
import HeadingText from "components/common/HeadingText/HeadingText";
import ButtonComponent from "components/common/Button/ButtonComponent";
import { Col, Container, Form, Row } from "reactstrap";
import SearchableSelect from "components/common/SearchableSelect/SearchableSelect";
import FormSectionLayout from "components/layouts/FormSectionLayout";
import Switch from "../../common/Switch/Switch";
import {
  ISearchSelectChange,
  IDeliveryDateCreationProps,
  IIdValuePairResponse,
} from "interfaces/Index";
import { normalizeDeliveryDatePayload } from "../../../normalizer/DeliveryDateNormalizer";
import "./DeliveryDatePage.scss";
import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import "react-datepicker/dist/react-datepicker.css";
import moment from "moment";
import Toaster from "components/common/Toaster/Toaster";
import LoaderComponent from "components/common/Loader/Loader";
import { AbilityContext } from "../../../config/Can";
import {
  ALERT_MESSAGE_END_TIME,
  ALERT_MESSAGE_EXPECTED_DELIVERY_DATE,
  ALERT_MESSAGE_START_TIME,
  DELIVERY_CONFIG_SUCCESSFUL,
  DELIVERY_DATE_STATUS,
  EXPECTED_DELIVERY_AT,
  ORDER_RECEIVED_END_TIME,
  ORDER_RECEIVED_START_TIME,
} from "./DeliveryDatePage.constants";

const DeliveryDatePage: React.FunctionComponent<IDeliveryDateCreationProps> = (
  props: IDeliveryDateCreationProps
) => {
  const ability = useContext(AbilityContext);

  const [showToaster, setToaster] = useState({
    isVisible: false,
    message: "",
    isError: false,
  });

  const yesterday = moment().subtract(1, "day");
  const today = moment().add(0, "day");

  const disablePastDt = (current: {
    isAfter: (arg0: moment.Moment) => any;
  }) => {
    return current.isAfter(yesterday);
  };

  const disablePastDt_Delivery = (current: {
    isAfter: (arg0: moment.Moment) => any;
  }) => {
    return current.isAfter(today);
  };

  const [warehouseIdState, setWarehouseState] = useState<IIdValuePairResponse>(
    props.warehouseCategories as any
  );

  const [city] = useState<ISearchSelectChange>();

  const [deliveryDateCreation, setDeliveryDateCreation] = useState<any>({
    warehouseId: warehouseIdState,
    orderReceivedStartTime: props.warehouseDeliveryDateConfig
      ? props.warehouseDeliveryDateConfig.orderReceivedStartTime
      : "",
    orderReceivedEndTime: props.warehouseDeliveryDateConfig
      ? props.warehouseDeliveryDateConfig.orderReceivedEndTime
      : "",
    expectedDeliveryAt: props.warehouseDeliveryDateConfig
      ? props.warehouseDeliveryDateConfig.expectedDeliveryAt
      : "",
    status: props.warehouseDeliveryDateConfig
      ? props.warehouseDeliveryDateConfig.status ===
        DELIVERY_DATE_STATUS.DEFAULT
        ? false
        : true
      : DELIVERY_DATE_STATUS.CUSTOM,
  });

  useEffect(() => {
    const { isWarehouseConfigAdded, isError } = props;
    if (isWarehouseConfigAdded && !isError) {
      const message = "Delivery configuration successful!";
      handleToaster(false, message);
      setTimeout(() => window.location.reload(), 5000);
    }
  }, [props.isWarehouseConfigAdded, props.isError]);

  function toUTC(dateString: string) {
    const date = new Date(dateString);
    const utcString = date.toISOString();
    return utcString.replace("Z", "").replace(".", "T");
  }

  function parseDate(dateString: string) {
    return Date.parse(dateString);
  }

  useEffect(() => {
    const { fetchCities, warehouseDeliveryDateConfig } = props;
    fetchCities();
    if (!warehouseDeliveryDateConfig) {
      const deliveryDateCreationObj: any = {
        expectedDeliveryAt: "",
        orderReceivedEndTime: "",
        orderReceivedStartTime: "",
        status: deliveryDateCreation.status,
        warehouseId: warehouseIdState.id,
      };
      setDeliveryDateCreation(deliveryDateCreationObj);
      return;
    }
    const {
      orderReceivedStartTime,
      orderReceivedEndTime,
      expectedDeliveryAt,
      status,
      warehouseId,
    } = warehouseDeliveryDateConfig;
    const deliveryDateCreationObject = {
      orderReceivedStartTime: parseDate(toUTC(orderReceivedStartTime)),
      orderReceivedEndTime: parseDate(toUTC(orderReceivedEndTime)),
      expectedDeliveryAt: parseDate(toUTC(expectedDeliveryAt)),
      status: status !== DELIVERY_DATE_STATUS.DEFAULT,
      warehouseId,
    };
    setDeliveryDateCreation(deliveryDateCreationObject);
  }, [
    props.isWarehouseCategoriesFetched,
    props.isFetchWarehouseDeliveryDateConfig,
  ]);

  const handleSelectedCityChange = ({ id }: IIdValuePairResponse) => {
    const { getWarehouseCategories } = props;
    getWarehouseCategories(id);
    setDeliveryDateCreation((prevState: any) => ({
      ...prevState,
      warehouseId: "",
    }));
  };

  const handleSelectedWarehouseChange = (e: IIdValuePairResponse) => {
    const { fetchWarehouseDeliveryDateConfig } = props;
    fetchWarehouseDeliveryDateConfig(e.id);
    setWarehouseState(e);
    setDeliveryDateCreation((prevState: any) => ({
      ...prevState,
      warehouseId: e.id,
    }));
  };

  const handleStatus = (e: React.ChangeEvent<HTMLInputElement>) => {
    const fieldId: string = e.target.id;
    setDeliveryDateCreation((prevState: any) => ({
      ...prevState,
      [fieldId]: !deliveryDateCreation[fieldId],
    }));
  };

  const handleToaster = (error: boolean, message: string) => {
    setToaster({
      isVisible: true,
      message: message,
      isError: error,
    });
  };

  let currentDate = new Date();

  const validateForm =
    props.dummyValidation ||
    !!(
      deliveryDateCreation.expectedDeliveryAt &&
      deliveryDateCreation.orderReceivedStartTime &&
      deliveryDateCreation.orderReceivedEndTime &&
      deliveryDateCreation.status &&
      deliveryDateCreation.status === ""
    ) ||
    currentDate < deliveryDateCreation.expectedDeliveryAt;

  const handleFieldUpdate = (field: string, value: Date) => {
    setDeliveryDateCreation({
      ...deliveryDateCreation,
      [field]: value,
    });
  };

  const handleDeliveryDate = (value: any) => {
    handleFieldUpdate(EXPECTED_DELIVERY_AT, value._d);
  };

  const handleFromDateTime = (value: any) => {
    handleFieldUpdate(ORDER_RECEIVED_START_TIME, value._d);
  };

  const handleToDateTime = (value: any) => {
    handleFieldUpdate(ORDER_RECEIVED_END_TIME, value._d);
  };

  const handleSubmit = () => {
    const { createUpdateWarehouseDeliveryDateConfig } = props;
    let deliveryDatePayload =
      normalizeDeliveryDatePayload(deliveryDateCreation);
    if (
      typeof deliveryDatePayload.orderReceivedEndTime ||
      typeof deliveryDatePayload.orderReceivedStartTime ||
      typeof deliveryDatePayload.expectedDeliveryAt == "number"
    ) {
      deliveryDatePayload.expectedDeliveryAt = new Date(
        deliveryDatePayload.expectedDeliveryAt
      );
      deliveryDatePayload.orderReceivedEndTime = new Date(
        deliveryDatePayload.orderReceivedEndTime
      );
      deliveryDatePayload.orderReceivedStartTime = new Date(
        deliveryDatePayload.orderReceivedStartTime
      );
    }

    if (deliveryDatePayload) {
      let orderReceivedStartTimeTimeString =
        deliveryDatePayload.orderReceivedStartTime.toTimeString();
      let orderReceivedEndTimeTimeString =
        deliveryDatePayload.orderReceivedEndTime.toTimeString();
      let orderReceivedStartTimeDateString =
        deliveryDatePayload.orderReceivedStartTime.toDateString();
      let orderReceivedEndTimeDateString =
        deliveryDatePayload.orderReceivedEndTime.toDateString();
      let currentDate = new Date();
      let currentTimeString = currentDate.toTimeString();
      let currentDateString = currentDate.toDateString();

      if (
        (orderReceivedStartTimeDateString === currentDateString &&
          orderReceivedStartTimeTimeString <= currentTimeString) ||
        (orderReceivedEndTimeDateString === currentDateString &&
          orderReceivedEndTimeTimeString <= currentTimeString) ||
        deliveryDatePayload.orderReceivedStartTime < currentDate ||
        deliveryDatePayload.orderReceivedEndTime < currentDate
      ) {
        const message = ALERT_MESSAGE_START_TIME;
        handleToaster(true, message);
        setTimeout(() => window.location.reload(), 5000);
      } else if (
        orderReceivedEndTimeDateString === orderReceivedStartTimeDateString &&
        orderReceivedEndTimeTimeString < orderReceivedStartTimeTimeString
      ) {
        const message = ALERT_MESSAGE_END_TIME;
        handleToaster(true, message);
        setTimeout(() => window.location.reload(), 5000);
      } else if (
        deliveryDatePayload.expectedDeliveryAt <
          deliveryDatePayload.orderReceivedEndTime ||
        deliveryDatePayload.orderReceivedEndTime >
          deliveryDatePayload.expectedDeliveryAt
      ) {
        const message = ALERT_MESSAGE_EXPECTED_DELIVERY_DATE;
        handleToaster(true, message);
        setTimeout(() => window.location.reload(), 5000);
      } else {
        createUpdateWarehouseDeliveryDateConfig(deliveryDatePayload);
        const message = DELIVERY_CONFIG_SUCCESSFUL;
        handleToaster(false, message);
        setTimeout(() => window.location.reload(), 5000);
      }
    }
  };

  const renderForm = () => {
    return (
      <Row className="form-container">
        {props.isLoading && <LoaderComponent />}
        <Toaster
          showToaster={showToaster.isVisible}
          toasterClass={
            showToaster.isError
              ? "toaster toaster-bg-danger"
              : "toaster toaster-bg-success"
          }
        >
          {showToaster.message || ""}
        </Toaster>
        <FormSectionLayout subTitleText="City details">
          <SearchableSelect
            label="city"
            options={props.citiesList}
            id="city"
            onChange={handleSelectedCityChange}
            selectedValue={city}
            placeholder="Select city"
            isSearchable={false}
          />
          <SearchableSelect
            label="warehouse"
            options={props.warehouseCategories}
            id="warehouseId"
            onChange={handleSelectedWarehouseChange}
            placeholder="Select warehouse"
            selectedValue={warehouseIdState}
            isSearchable={false}
          />
        </FormSectionLayout>
        <FormSectionLayout subTitleText="Custom settings">
          <Switch
            id="status"
            label="Custom"
            checked={deliveryDateCreation.status}
            handleSwitch={handleStatus}
            statusVal1="ACTIVE"
            statusVal2="INACTIVE"
          />
        </FormSectionLayout>
        <FormSectionLayout subTitleText="">
          <section className="form-group search-container">
            <label className="block text-uppercase label">
              Orders received between
            </label>
            <Row className="round">
              <Col lg="6" id="from-date-container">
                <Datetime
                  value={deliveryDateCreation.orderReceivedStartTime}
                  onChange={handleFromDateTime}
                  inputProps={{ placeholder: "Start Date & Time" }}
                  isValidDate={disablePastDt}
                />
              </Col>
              <Col lg="6" id="to-date-time-container">
                <Datetime
                  value={deliveryDateCreation.orderReceivedEndTime}
                  onChange={handleToDateTime}
                  inputProps={{ placeholder: "End Date & Time" }}
                  isValidDate={disablePastDt}
                />
              </Col>

              <Col lg="12">
                <Row className="mt-5">
                  <Col lg="6" id="delivery-date-container">
                    <label className="block text-uppercase label">
                      Delivery Date
                    </label>
                    <Datetime
                      value={deliveryDateCreation.expectedDeliveryAt}
                      onChange={handleDeliveryDate}
                      inputProps={{ placeholder: "Select Date" }}
                      isValidDate={disablePastDt_Delivery}
                      timeFormat={false}
                    />
                  </Col>
                </Row>
              </Col>
            </Row>
          </section>
          {renderFormButton()}
        </FormSectionLayout>
      </Row>
    );
  };

  const renderFormButton = () => {
    return (
      <Row className="mt-5">
        <Col lg="6">
          <div id="handle-submit-container">
            {ability.can("read", "warehouse") && (
              <ButtonComponent
                buttonClass="large-button btn-primary handle-submit"
                buttonText={
                  props.warehouseDeliveryDateConfig ? "Update" : "Create"
                }
                onClickHandler={handleSubmit}
                isDisable={props.isLoading || !validateForm}
              />
            )}
          </div>
        </Col>
      </Row>
    );
  };

  return (
    <Container fluid className="deliveryDate-container">
      <Row>
        <Col lg="5">
          <div className="deliveryDate-main-heading">
            <HeadingText text={"Delivery date"} isMainHeading={true} />
          </div>
        </Col>
      </Row>

      <Row className="form-container ml-0 mr-0">
        <Col xs="12">
          <Form>{renderForm()}</Form>
        </Col>
      </Row>
    </Container>
  );
};

DeliveryDatePage.defaultProps = {
  dummyValidation: false,
};

export default DeliveryDatePage;
